import { themeVars } from '@cointracker/styleguide';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import type { RefObject } from 'react';
import { useEffect } from 'react';

export const BACKGROUND_FADE_VARIABLE_NAME = '--background-fade';

// Set `data-bg-color={}` on elements that should change the background color when scrolled into view
export const useBackgroundFade = (
  targetRefs: RefObject<HTMLElement | null>[] = [], // Accept refs to elements that should also receive this background color
) => {
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const sections = document.querySelectorAll<HTMLElement>('[data-bg-color]');

    const background = window
      .getComputedStyle(document.body)
      .getPropertyValue(themeVars.background.match(/--[\w-]+/)?.[0] ?? '');

    // Function to apply background color to html and target refs
    const applyColor = (color: string) => {
      gsap.set('html', {
        backgroundColor: color,
        [BACKGROUND_FADE_VARIABLE_NAME]: color,
      });

      // Apply color to all target refs
      targetRefs.forEach((ref) => {
        if (ref.current) {
          gsap.set(ref.current, { backgroundColor: color });
        }
      });
    };

    // Set initial background
    applyColor(background);

    sections.forEach((section, index) => {
      if (!(section instanceof HTMLElement)) return;

      ScrollTrigger.create({
        trigger: section,
        start: 'top center',
        end: 'bottom center',
        onEnter: () => {
          applyColor(section.dataset.bgColor || background);
        },
        onLeaveBack: () => {
          applyColor(sections[index - 1]?.dataset?.bgColor || background);
        },
        onRefresh: () => {
          applyColor(background);
        },
      });
    });

    return () => {
      // Clean up by clearing properties on target refs
      targetRefs.forEach((ref) => {
        if (ref.current) {
          gsap.set(ref.current, { clearProps: 'backgroundColor' });
        }
      });
    };
  }, [targetRefs]);
};
